/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {MarketAreaPlanningMarketMap} from '../models/market-area-planning-market-map.model';
import {MarketAreaReport} from '../models/reports/market-area-report';
import {ConfigService} from './config.service';

@Injectable({
  providedIn: 'root'
})
export class MarketAreaService {

  constructor(private readonly configService: ConfigService,
              private readonly http: HttpClient) {
  }

  /**
   * Sends a get request to get a list of Market Area records based on region
   * @param region - string
   */
  getMarketAreasByRegion(region: string): Observable<string[]> {
    const params = new HttpParams().set('region', region);
    return this.http.get<string[]>(`${this.configService.baseApiUrl}market-area`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * gets the market areas that the user can access filtered by a vehicle scenario
   * @param vehScenId The id of the associated vehicle scenario
   * @param gmid The user's GMID
   */
  getMarketAreasByVehicleScenario(vehScenId: number, gmid: string) {
    const params = new HttpParams().set('veh-scen-id', vehScenId).set('gmid', gmid);
    return this.http.get<string[]>(`${this.configService.baseApiUrl}market-area`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a post request to add a new Market Area record
   * @param mktArea - string
   */
  addMarketArea(mktArea: string): Observable<any> {
    const params = new HttpParams().set('mktArea', mktArea);
    return this.http.post<any>(`${this.configService.baseApiUrl}market-area`, null, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a post request to add a list of Market Area Planning Market Mapping records
   * @param marketAreaPlanningMarketMapList - MarketAreaPlanningMarketMap[]
   */
  addPlanningMarketMapping(marketAreaPlanningMarketMapList: MarketAreaPlanningMarketMap[]): Observable<any> {
    return this.http.post<any>(`${this.configService.baseApiUrl}market-area/planning-market-mapping`, marketAreaPlanningMarketMapList)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a delete request to delete a Market Area record
   * @param mktArea - string
   */
  deleteMarketArea(mktArea: string): Observable<any> {
    const params = new HttpParams().set('mktArea', mktArea);
    return this.http.delete<any>(`${this.configService.baseApiUrl}market-area`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a delete request to delete a list of Market Area Planning Market Mapping records
   * @param mktArea - string
   */
  deletePlanningMarketMappingByMarketArea(mktArea: string): Observable<any> {
    const params = new HttpParams().set('mktArea', mktArea);
    return this.http.delete<any>(`${this.configService.baseApiUrl}market-area/planning-market-mapping`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Gets market areas by region and scenario id
   * @param region the region to filter by
   * @param indScenId the scenario id to filter by
   */
  getMarketAreasByRegionAndIndustryScenario(region: string, indScenId: number, gmid: string) {
    const params = new HttpParams().set('region', region).set('ind-scen-id', indScenId).set('gmid', gmid);
    return this.http.get<any>(`${this.configService.baseApiUrl}market-area`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a get request to get a Market Area Report
   */
  getMarketAreaReport(): Observable<MarketAreaReport[]> {
    return this.http.get<MarketAreaReport[]>(`${this.configService.baseApiUrl}market-area/report`)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Handles the errors produced by the backend
   * @param errorResponse The error produced by the API to be handled by the frontend
   */
  private handleError(errorResponse: HttpErrorResponse): Observable<any> {
    let errorMessage = 'An unexpected error has occurred.';
    if (errorResponse.error && errorResponse.error.message) {
      errorMessage = errorResponse.error.message;
    }
    return throwError(errorMessage);
  }
}
